import { INSPIRATIONAL_TEMPLATE_MAP as EN_MAP } from './i18n/inspirationalTemplateMap.en';
import { INSPIRATIONAL_TEMPLATE_MAP as ES_MAP } from './i18n/inspirationalTemplateMap.es';
import { INSPIRATIONAL_TEMPLATE_MAP as FR_MAP } from './i18n/inspirationalTemplateMap.fr';
import { INSPIRATIONAL_TEMPLATE_MAP as DE_MAP } from './i18n/inspirationalTemplateMap.de';
import { INSPIRATIONAL_TEMPLATE_MAP as JA_MAP } from './i18n/inspirationalTemplateMap.ja';
import { INSPIRATIONAL_TEMPLATE_MAP as PT_MAP } from './i18n/inspirationalTemplateMap.pt';
import { AdditionalFields } from './domain/entities/inspirationalTemplate';

export enum CategorySlug {
  DEFAULT = 'all-templates',
  DESIGNED_TEMPLATES = 'designed-templates',
  INSPIRATIONAL = 'inspirational',
  WIREFRAMES = 'wireframes',
  ECOMMERCE = 'ecommerce',
  ONE_PAGE = 'one-page',
  PORTFOLIO_TYPE = 'type-portfolio',
  COMPANY_SITE = 'company-site',
  CMS = 'cms',
  BUSINESS = 'business',
  TECHNOLOGY = 'technology',
  CREATIVE = 'creative',
  FASHION_BEAUTY = 'fashion-beauty',
  STORES = 'stores',
  BOOKINGS = 'bookings',
  PORTFOLIO_FEATURE = 'feature-portfolio',
  BLOG = 'blog',
  EVENTS = 'events',
  PRICING_PLANS = 'pricing-plans',
  RESTAURANTS = 'restaurants',
  DARK = 'dark',
  MINIMAL = 'minimal',
  ILLUSTRATIVE = 'illustrative',
  TYPOGRAPHY = 'typography',
  PANTONE = 'wix-studio-x-pantone',
  // industry new categories:
  DESIGN = 'design',
  FITNESS = 'fitness',
  REAL_ESTATE = 'real-estate',
  HEALTH = 'health',
  PHOTOGRAPHY = 'photography',
  CONSTRUCTION = 'construction',
  NON_PROFIT = 'non-profit',
  MAINTENANCE = 'maintenance',
  BEAUTY = 'beauty',
  EDUCATION = 'education',
  FOOD_DRINKS = 'food-drinks',
  TRAVEL = 'travel',
  // style new categories:
  BRIGHT = 'bright',
  GRADIENT = 'gradient',
  BLACK_N_WHITE = 'b-w',
  NEON = 'neon',
  // features new categories:
  DONATIONS = 'donations',
  GROUPS = 'groups',
  ONLINE_PROGRAMS = 'online-programs',
}

export const isCategorySlug = (slug: string): slug is CategorySlug =>
  Object.values(CategorySlug).includes(slug as CategorySlug);

export const BLANK_TEMPLATE_META_SITE_ID =
  'fe86a14e-ef67-49b4-a409-d086f3abaa1a';
export const EDIT_TEMPLATE_URL = 'https://editor.wix.com/edit-template/from';
export const SITELESS_EDIT_TEMPLATE_URL =
  'https://editor.wix.com/edit-template/from-intro-siteless';
export const SOCIAL_MEDIA_THUMBNAIL_URL =
  'https://wixmp-530a50041672c69d335ba4cf.wixmp.com/studio/social-media/wix_studio_social_thumbnail_1200X628.jpg';
export const SUPPORTED_LANGUAGES = [
  'en',
  'es',
  'fr',
  'de',
  'ja',
  'pt',
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const isSupportedLanguage = (lang: string): lang is SupportedLanguage =>
  SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);

export const INSPIRATIONAL_CATEGORY_SLUG = 'inspirational';

export const INSPIRATIONAL_TEMPLATES_LANGUAGE_MAP: Record<
  SupportedLanguage,
  Record<string, AdditionalFields>
> = {
  de: DE_MAP,
  es: ES_MAP,
  fr: FR_MAP,
  ja: JA_MAP,
  pt: PT_MAP,
  en: EN_MAP,
};

interface CategoryGroupConfig {
  title: string;
  categorySlugs: string[];
}

export const TOP_BAR_CATEGORY_GROUPS: CategoryGroupConfig[] = [
  {
    title: 'app.topBar.menu.buckets.main',
    categorySlugs: [
      CategorySlug.DEFAULT,
      CategorySlug.WIREFRAMES,
      CategorySlug.DESIGNED_TEMPLATES,
      CategorySlug.INSPIRATIONAL,
      CategorySlug.PANTONE,
    ],
  },
  {
    title: 'app.topBar.menu.buckets.type',
    categorySlugs: [
      CategorySlug.ONE_PAGE,
      CategorySlug.ECOMMERCE,
      CategorySlug.PORTFOLIO_TYPE,
      CategorySlug.COMPANY_SITE,
      CategorySlug.CMS,
    ],
  },
  {
    title: 'app.topBar.menu.buckets.industry',
    categorySlugs: [
      CategorySlug.BUSINESS,
      CategorySlug.TECHNOLOGY,
      CategorySlug.FASHION_BEAUTY,
      CategorySlug.CREATIVE,
      //
      CategorySlug.DESIGN,
      CategorySlug.FITNESS,
      CategorySlug.REAL_ESTATE,
      CategorySlug.HEALTH,
      CategorySlug.PHOTOGRAPHY,
      CategorySlug.CONSTRUCTION,
      CategorySlug.NON_PROFIT,
      CategorySlug.MAINTENANCE,
      CategorySlug.BEAUTY,
      CategorySlug.EDUCATION,
      CategorySlug.FOOD_DRINKS,
      CategorySlug.TRAVEL,
    ],
  },
  {
    title: 'app.topBar.menu.buckets.features',
    categorySlugs: [
      CategorySlug.STORES,
      CategorySlug.BOOKINGS,
      CategorySlug.PORTFOLIO_FEATURE,
      CategorySlug.BLOG,
      CategorySlug.EVENTS,
      CategorySlug.PRICING_PLANS,
      CategorySlug.RESTAURANTS,
      CategorySlug.DONATIONS,
      CategorySlug.GROUPS,
      CategorySlug.ONLINE_PROGRAMS,
    ],
  },
  {
    title: 'app.topBar.menu.buckets.style',
    categorySlugs: [
      CategorySlug.DARK,
      CategorySlug.BRIGHT,
      CategorySlug.GRADIENT,
      CategorySlug.BLACK_N_WHITE,
      CategorySlug.NEON,
      CategorySlug.MINIMAL,
      CategorySlug.ILLUSTRATIVE,
      CategorySlug.TYPOGRAPHY,
    ],
  },
];

const ALL_AND_DESIGNED_TEMPLATES_MARKETPLACE_TEMPLATE_IDS = [
  'd1d128c3-380e-4022-b658-13b34bf676b6',
  'f21d8c0a-5f96-4e8b-a3b5-43517b18f445',
  '1fcaa142-b756-4deb-8a16-b09703f2560a',
  'a8d11728-4adf-4e9b-9683-c6ab739b5b5d',
  'b711ea7e-8491-413b-a2ed-7010a850ddce',
  'bbf7f248-87b2-430f-8dd1-48d05ea08c53',
  '41651d09-fd0c-4a23-a36e-e6fc41a3bd8b',
  '8a637ed2-3200-4c95-9a52-bf638a4934f7',
];

export const CATEGORY_SLUGS_TO_MARKETPLACE_TEMPLATE_IDS: Record<
  string,
  string[]
> = {
  [CategorySlug.DEFAULT]: ALL_AND_DESIGNED_TEMPLATES_MARKETPLACE_TEMPLATE_IDS,
  [CategorySlug.DESIGNED_TEMPLATES]:
    ALL_AND_DESIGNED_TEMPLATES_MARKETPLACE_TEMPLATE_IDS,
  [CategorySlug.ECOMMERCE]: [
    '41651d09-fd0c-4a23-a36e-e6fc41a3bd8b',
    'ba4c8670-31d4-4f4e-91c0-c8c68681dea7',
    '6f2ef2e7-bdc0-4226-aa60-00859745fcfb',
    '95788be1-648c-4268-9258-227be35bafd6',
    'a9812874-3e6e-452d-9eaa-4c4f53dcd9a5',
    'ef4694c6-f90a-4b30-a7d6-967394feb4f6',
    '8f20a013-8d45-4976-a88f-5b2b5694ddc6',
    '4d3e0c94-8cc6-4c2c-b4c6-9cd7e6f5369c',
    '684e66c-7391-4c25-ba4f-ea03ea2eac2e',
    'c43ec5c9-644a-4d21-b554-5a281b0eb9e3',
  ],
  [CategorySlug.PORTFOLIO_TYPE]: [
    'a8d11728-4adf-4e9b-9683-c6ab739b5b5d',
    '6d076401-d635-4683-880e-60ecb4567c31',
    '109689d8-bd6f-49b8-97b4-c88c615c9988',
    '3fe018b7-8d9c-472d-8399-219844acc785',
    'fbe20a9e-6ab8-4d7f-be0e-17bd1b478e9c',
    '2cfb76a7-40fd-468e-9fde-a8b3f1c52795',
    '5028f77f-422b-4f2a-ab52-afb5eb997b18',
    'acab098d-95be-4f8a-a715-b5823be4656e',
    '46c8d638-0f26-4673-bb3f-4f1b716d4a7c',
    '6426a774-5cab-408c-8bf3-d619863271ff',
  ],
  [CategorySlug.BUSINESS]: [
    'bb7502b4-8f58-4b8f-bf1f-f452980c15c9',
    '11fe9bd9-40a5-492f-821d-34e77972fb2f',
    '8441bd31-3704-4827-b071-7e76c37cceee',
    'c5d894e8-fe67-4734-97ff-644c0e87abe3',
    '9ef4a543-a60e-4e44-9234-826144f48c7b',
    'fda00131-c022-4405-b76a-ec42c95aeaaf',
    'cdc35823-0067-44e8-8440-8e8e8e7db84e',
    'b80ba53b-efce-49d4-abda-a28151e0f5dd',
    '7221e07f-6b33-41d1-a115-67a10588d77c',
    '9301069f-0d48-4d07-8863-82ab37c3fc39',
  ],
  [CategorySlug.ONE_PAGE]: [
    'd1d128c3-380e-4022-b658-13b34bf676b6',
    '1fcaa142-b756-4deb-8a16-b09703f2560a',
    '5fe7dba0-19d9-453c-a634-11eca408de93',
    '5ef83340-33e2-45c7-954e-c23952c5aa4a',
    'b711ea7e-8491-413b-a2ed-7010a850ddce',
    'b6fb8168-2e50-48b5-824a-d51f68d5e225',
    '25f12084-3ab3-47ed-992f-0739aee52fe2',
    'efb6f7fa-c405-4bc0-a0b8-e202bdb597d0',
    '5c852240-8ea0-4517-bdb4-8681bf0b02a2',
    '6b25b31b-a1f1-47a6-a1a2-9b7b6452c882',
  ],
  [CategorySlug.CREATIVE]: [
    '1fcaa142-b756-4deb-8a16-b09703f2560a',
    'a8d11728-4adf-4e9b-9683-c6ab739b5b5d',
    '8441bd31-3704-4827-6071-7e76c37cceee',
    'c5d894e8-fe67-4734-97ff-644c0e87abe3',
    '60d19009-0ba0-45e3-abfd-28549c1acc24',
    '325960aa-f701-4de9-a05d-7fb9c46118b5',
    '6d076401-d635-4683-880e-60ecb4567c31',
    'cdc35823-0067-44e8-8440-8e8e8e7db84e',
    '081674b3-f35a-45ca-8331-9cabf5bfe0a4',
    '3fe018b7-8d9c-472d-8399-219844acc785',
  ],
  [CategorySlug.MINIMAL]: [
    'b711ea7e-8491-413b-a2ed-7010a850ddce',
    '60d19009-0ba0-45e3-abfd-28549c1acc24',
    'cdc35823-0067-44e8-8440-8e8e8e7db84e',
    'ee832e96-a450-4dcf-9f38-9c235f82e448',
    'f21d8c0a-5f96-4e8b-a3b5-43517b18f445',
    'ba4c8670-31d4-4f4e-91c0-c8c68681dea7',
    '9ef4a543-a60e-4e44-9234-826144f48c7b',
    '6d076401-d635-4683-880e-60ecb4567c31',
    '8f28e65b-471c-416f-a193-5cc34694c48a',
  ],
};

export const ALL_MARKETPLACE_TEMPLATE_IDS = [
  ...new Set(Object.values(CATEGORY_SLUGS_TO_MARKETPLACE_TEMPLATE_IDS).flat()),
];
